import { Badge, Tooltip } from '@apollo/orbit';
import React from 'react';
import { Link } from 'react-router-dom-v5-compat';

import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { useUpgradeContext } from 'src/hooks/useUpgradeContext';
import { BillingPlanKind } from 'src/lib/graphqlTypes/types';

import { getPlanBadgeProperties } from './helpers';

interface PlanBadgeProps {
  accountId: string;
  planName?: string;
  planKind?: BillingPlanKind;
  daysRemaining?: number;
}

/**
 * Renders a badge that represents the user's billing plan
 * The badge may include a tooltip and be interactive based on plan kind.
 */
export const PlanBadge = ({
  accountId,
  planName,
  planKind,
  daysRemaining,
}: PlanBadgeProps) => {
  const { upgradeRoute } = useUpgradeContext();

  if (!planName || !planKind) return null;

  const badgeProps = getPlanBadgeProperties(planName, planKind);

  if (!badgeProps) return null;

  const { label, tooltip, isInteractive } = badgeProps;

  const formattedLabel =
    daysRemaining && daysRemaining > 0 // Append days remaining to the label if applicable
      ? `${label} (${daysRemaining} day${daysRemaining > 1 ? 's' : ''} left)`
      : label;

  const badgeElement = (
    <Badge variant="info" className="mx-2" data-testid="plan-badge">
      {formattedLabel}
    </Badge>
  );

  if (isInteractive) {
    const linkElement = <Link to={upgradeRoute}>{badgeElement}</Link>;

    const interactiveContent = (
      <Tooltip label={tooltip} isDisabled={!tooltip}>
        {linkElement}
      </Tooltip>
    );

    return (
      <PermissionGuard
        accountId={accountId}
        accountPermissions="canSetConstrainedPlan"
        fallback={badgeElement}
      >
        {interactiveContent}
      </PermissionGuard>
    );
  }

  return tooltip ? (
    <Tooltip label={tooltip}>{badgeElement}</Tooltip>
  ) : (
    badgeElement
  );
};
